import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import axios from 'axios';
import logger from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Test user for UI testing - uses a valid UUID format
const TEST_USER = {
  id: '00000000-0000-0000-0000-000000000001',
  email: 'test@michezonet.com',
  user_metadata: { first_name: 'Test', last_name: 'User' }
};

/**
 * Security Note: 
 * Token storage uses sessionStorage instead of localStorage for improved XSS protection.
 * For production environments, consider implementing httpOnly cookie-based auth
 * via backend endpoints for maximum security.
 */

// Token storage utilities - using sessionStorage for better security
const TokenStorage = {
  setToken: (token) => {
    if (typeof window !== 'undefined' && token) {
      sessionStorage.setItem('access_token', token);
    }
  },
  getToken: () => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('access_token');
    }
    return null;
  },
  removeToken: () => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('access_token');
    }
  }
};

export const AuthProvider = ({ children }) => {
  // Check for test mode via URL parameter
  const isTestMode = typeof window !== 'undefined' && 
    new URLSearchParams(window.location.search).get('test_mode') === 'true';
  
  const [user, setUser] = useState(isTestMode ? TEST_USER : null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(!isTestMode);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${API}/users/profile/${userId}`);
      setProfile(response.data);
    } catch (err) {
      logger.error('Error fetching profile:', err);
    }
  }, []);

  useEffect(() => {
    // Skip Supabase auth check in test mode
    if (isTestMode) {
      setLoading(false);
      return;
    }
    
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Check current session
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          TokenStorage.setToken(session.access_token);
          await fetchProfile(session.user.id);
        }
      } catch (err) {
        logger.error('Session check error:', err);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          setUser(session.user);
          TokenStorage.setToken(session.access_token);
          await fetchProfile(session.user.id);
        } else {
          setUser(null);
          setProfile(null);
          TokenStorage.removeToken();
        }
        setLoading(false);
      }
    );

    return () => subscription?.unsubscribe();
  }, [fetchProfile, isTestMode]);

  const signUp = useCallback(async (email, password, userData = {}) => {
    setError(null);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (signUpError) throw signUpError;
      
      // Create profile in backend
      if (data.user) {
        await axios.post(`${API}/users/profile`, {
          id: data.user.id,
          email: data.user.email,
          ...userData
        });
      }
      
      return { success: true, message: 'Check your email for confirmation' };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const signIn = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (signInError) throw signInError;
      
      setUser(data.user);
      TokenStorage.setToken(data.session.access_token);
      
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
      TokenStorage.removeToken();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    setError(null);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      });
      
      if (resetError) throw resetError;
      
      return { success: true, message: 'Check your email for reset link' };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const value = useMemo(() => ({
    user,
    profile,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    isAuthenticated: !!user
  }), [user, profile, loading, error, signUp, signIn, signOut, resetPassword]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
